import { fetchWithTimeout, runHttpPing } from './http-ping.js';
import { measureDownload, measureUpload } from './bandwidth.js';

function percentile(samples, p) {
  if (samples.length === 0) return 0;
  const sorted = [...samples].sort((a, b) => a - b);
  // Nearest-rank percentile per the standard form: ceil(p/100 * n) - 1, clamped.
  // Matches the formula used in src/probes/websocket.js.
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.ceil((p / 100) * sorted.length) - 1));
  return sorted[idx];
}

export function computeBufferbloatIncrease(idleSamples, loadedSamples) {
  if (!idleSamples.length || !loadedSamples.length) {
    return { idleP50Ms: 0, loadedP95Ms: 0, increaseMs: null };
  }
  const idleP50 = percentile(idleSamples, 50);
  const loadedP95 = percentile(loadedSamples, 95);
  return {
    idleP50Ms: idleP50,
    loadedP95Ms: loadedP95,
    increaseMs: Math.max(0, loadedP95 - idleP50)
  };
}

export async function runBufferbloat({
  serverUrl,
  idleDurationMs = 30_000,
  loadedDurationMs = 30_000,
  pingIntervalMs = 500,
  downloadBytes = 50 * 1024 * 1024,
  uploadBytes = 10 * 1024 * 1024,
  fetchImpl = fetch,
  sleepImpl = ms => new Promise(r => setTimeout(r, ms)),
  onPhase = () => {}
}) {
  // Phase 1: idle pings.
  onPhase('idle');
  const idle = await runHttpPing({
    serverUrl,
    durationMs: idleDurationMs,
    intervalMs: pingIntervalMs,
    fetchImpl,
    sleepImpl
  });

  // Phase 2: saturate the link with simultaneous download + upload while pinging.
  onPhase('loaded');
  const loadedPings = [];
  let loadedRunning = true;
  const loadedDeadline = Date.now() + loadedDurationMs;

  const pingLoop = (async () => {
    while (loadedRunning && Date.now() < loadedDeadline) {
      const t0 = Date.now();
      try {
        const res = await fetchWithTimeout(`${serverUrl}/ping?t=${t0}`, {
          fetchImpl,
          timeoutMs: Math.min(2_000, Math.max(1, loadedDeadline - Date.now())),
          options: { cache: 'no-store' }
        });
        if (res.ok) {
          await res.json();
          loadedPings.push(Date.now() - t0);
        }
      } catch {
        // Network errors during saturation are themselves a signal but don't stop the probe.
      }
      const waitMs = Math.min(pingIntervalMs, Math.max(0, loadedDeadline - Date.now()));
      if (waitMs > 0) await sleepImpl(waitMs);
    }
  })();

  const transferTimeoutMs = Math.max(1, loadedDurationMs);
  const dlPromise = measureDownload({
    serverUrl,
    bytes: downloadBytes,
    timeoutMs: transferTimeoutMs,
    fetchImpl
  }).catch(err => ({ error: String(err), bytes: 0, durationMs: 0, mbps: 0, completed: false }));
  const upPromise = measureUpload({
    serverUrl,
    bytes: uploadBytes,
    timeoutMs: transferTimeoutMs,
    fetchImpl
  }).catch(err => ({ error: String(err), bytes: 0, durationMs: 0, mbps: 0, completed: false }));

  // Run for at most loadedDurationMs even if dl/up haven't finished.
  await Promise.race([
    Promise.allSettled([dlPromise, upPromise]),
    sleepImpl(loadedDurationMs)
  ]);

  loadedRunning = false;
  await pingLoop;
  const [dlResult, upResult] = await Promise.all([dlPromise, upPromise]);

  const verdict = computeBufferbloatIncrease(idle.samples, loadedPings);

  return {
    idle,
    loadedPings,
    download: dlResult,
    upload: upResult,
    ...verdict
  };
}
